import { MarkChatReadOutlined } from "@mui/icons-material";
import { Button, Form, InputNumber, Modal, Switch, Tag, message } from "antd";
import { memo, useEffect, useRef, useState } from "react";
import { connect } from "react-redux";
import api from "../../../api/api";
import DataTable from "../../../Components/DataTable/DataTable";
import CheckPermission from "../../../Util/CheckPermission";
import styles from "./SmsLines.module.css";

const yesNo = (value) => (
  <Tag color={value ? "green" : "default"}>{value ? "بله" : "خیر"}</Tag>
);

const SmsLines = ({ canEdit }) => {
  const formRef = useRef(null);
  const [lines, setLines] = useState([]);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const columns = [
    {
      title: "شماره",
      dataIndex: "number",
      searchable: true,
      render: (value) => <span dir="ltr">{value}</span>,
    },
    {
      title: "سرویس‌دهنده",
      dataIndex: "provider_title",
      searchable: true,
      render: (value) => value || "—",
    },
    {
      title: "نوع",
      dataIndex: "is_kavenegar_lookup",
      render: (value) => (value ? "لوکاپ کاوه‌نگار" : "ارسال عادی"),
    },
    {
      title: "فعال",
      dataIndex: "active",
      render: yesNo,
    },
    {
      title: "اولویت",
      dataIndex: "priority",
      render: (value) => (value == null ? "—" : value),
    },
    {
      title: "ارسال متن لینک دار/بدون لینک",
      dataIndex: "link_allowed",
      render: (value) => (value ? "متن لینک دار" : "متن بدون لینک"),
    },
    ...(canEdit
      ? [
          {
            title: "عملیات",
            render: (_, record) => (
              <Button type="primary" onClick={() => setEditing(record)}>
                ویرایش
              </Button>
            ),
          },
        ]
      : []),
  ];

  useEffect(() => {
    loadLines();
  }, []);

  const loadLines = () => {
    api
      .get("/smsline/manage")
      .then((res) => {
        setLines(Array.isArray(res.data) ? res.data : []);
      })
      .catch((error) => {
        message.error(
          error.response?.data?.error?.message || "خطا در دریافت خطوط پیامک"
        );
      });
  };

  const save = async () => {
    if (!editing || !formRef.current) return;

    let values;
    try {
      values = await formRef.current.validateFields();
    } catch (error) {
      return;
    }

    setSaving(true);
    api
      .post("/smsline/edit/" + editing.id, {
        active: values.active,
        priority: values.priority,
        link_allowed: values.link_allowed,
      })
      .then((res) => {
        if (res.data?.success) {
          message.success("خط پیامک ویرایش شد.");
          setEditing(null);
          loadLines();
          return;
        }
        message.error("خطا در ویرایش خط پیامک");
      })
      .catch((error) => {
        message.error(
          error.response?.data?.error?.message || "خطا در ویرایش خط پیامک"
        );
      })
      .finally(() => setSaving(false));
  };

  return (
    <>
      <DataTable
        id="SmsLines"
        dataSource={lines}
        setDataSource={setLines}
        columns={columns}
        reload={loadLines}
        recordCreatorDisabled
        header={{
          title: "خطوط پیامک",
          avatar: { icon: <MarkChatReadOutlined />, size: "large" },
        }}
      />
      <Modal
        title="ویرایش خط پیامک"
        open={Boolean(editing)}
        destroyOnClose
        confirmLoading={saving}
        okText="ذخیره"
        cancelText="انصراف"
        onOk={save}
        onCancel={() => {
          if (!saving) setEditing(null);
        }}
      >
        {editing ? (
          <>
            <div className={styles.readonly}>
              <div className={styles.field}>
                <span className={styles.label}>شماره</span>
                <span dir="ltr">{editing.number}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>سرویس‌دهنده</span>
                <span>{editing.provider_title || "—"}</span>
              </div>
              <div className={styles.field}>
                <span className={styles.label}>نوع</span>
                <span>
                  {editing.is_kavenegar_lookup
                    ? "لوکاپ کاوه‌نگار"
                    : "ارسال عادی"}
                </span>
              </div>
            </div>
            <Form
              key={editing.id}
              ref={formRef}
              layout="vertical"
              initialValues={{
                active: editing.active,
                priority: editing.priority,
                link_allowed: editing.link_allowed,
              }}
            >
              <Form.Item
                name="active"
                label="فعال"
                valuePropName="checked"
              >
                <Switch />
              </Form.Item>
              <Form.Item
                name="priority"
                label="اولویت"
                rules={[
                  { required: true, message: "اولویت را وارد کنید." },
                  {
                    validator: (_, value) => {
                      const max = lines.length;
                      if (
                        !Number.isInteger(value) ||
                        value < 1 ||
                        value > max
                      ) {
                        return Promise.reject(
                          new Error(`اولویت باید بین ۱ و ${max} باشد.`)
                        );
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
              >
                <InputNumber
                  min={1}
                  max={lines.length}
                  precision={0}
                  style={{ width: "100%" }}
                />
              </Form.Item>
              <Form.Item
                name="link_allowed"
                label="ارسال متن لینک دار/بدون لینک"
                valuePropName="checked"
              >
                <Switch
                  className={styles.linkSwitch}
                  checkedChildren="متن لینک دار"
                  unCheckedChildren="متن بدون لینک"
                />
              </Form.Item>
            </Form>
          </>
        ) : null}
      </Modal>
    </>
  );
};

const mapStateToProps = (state) => {
  return {
    canEdit: CheckPermission(
      "sms_line_edit",
      state.userData ? state.userData.permissions : []
    ),
  };
};

export default connect(mapStateToProps)(memo(SmsLines));
